import { useState, useEffect, useRef } from "react";
import { useRollbar } from "@rollbar/react";
import { ZUORA_PUBLISHABLE_KEY } from "@config";
import createZuoraPaymentSession from "@api/getZuoraToken";
import AddCardModal from "@components/PaymentMethods/AddCardModal";
import useSegment from "@hooks/useSegment";
import { ANALYTICS_EVENTS, ANALYTICS_LOCATIONS } from "@appTypes/analytics";

const ZUORA_CONTAINER_ID = "zuora-payment-form";

const useHandleAddCard = (onSuccess: (paymentMethodId: string) => void) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const rollbar = useRollbar();
  const { trackEvent } = useSegment();

  useEffect(() => {
    if (!isOpen) {
      setIsLoading(true);
      setError(null);
      return;
    }

    if (!window.Zuora || !ZUORA_PUBLISHABLE_KEY) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    // Tracks whether the modal was closed specifically to reveal the 3DS2
    // challenge iframe. Only when this is true should onComplete trigger the
    // success callback — a user-initiated close (ESC / backdrop) must not.
    let closedForChallenge = false;
    let bodyObserver: MutationObserver | null = null;
    const zuora = window.Zuora(ZUORA_PUBLISHABLE_KEY);

    const initializePaymentForm = async () => {
      try {
        const form = await zuora.createPaymentForm({
          currency: "USD",
          amount: "1.00",
          createPaymentSession: () => createZuoraPaymentSession(),
          onComplete: (result) => {
            if (result.success && result.paymentMethodId) {
              // Only invoke the success callback when the close was 3DS2-driven.
              // If the user closed the modal manually before the challenge
              // completed, closedForChallenge is false and we skip the callback.
              if (closedForChallenge) {
                onSuccessRef.current(result.paymentMethodId);
              }
              if (!cancelled) {
                setIsOpen(false);
              }
            } else if (!result.success && result.error && !cancelled) {
              setError(result.error.message);
              setIsOpen(true);
            }
          },
        });
        if (!cancelled) {
          form.mount(`#${ZUORA_CONTAINER_ID}`);
          setIsLoading(false);

          // When the user clicks Pay, Zuora appends its 3DS2 iframes directly
          // to document.body. Our modal sits above them (MUI z-index), blocking
          // the user from completing the challenge. Close the modal as soon as a
          // Zuora-owned iframe appears so the challenge is visible.
          // onComplete fires after the challenge resolves and handles success/error.
          //
          // Zuora SDK (zuora.js 1.4.0) appends two known iframes to document.body:
          //   1. zuora-3ds2-integration-<sessionId>  — device-session iframe (0×0, absolute)
          //   2. Spinner iframe (no id, position:fixed, zIndex:9999, 100%×100%)
          // We match both to avoid triggering on unrelated third-party iframes.
          bodyObserver = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
              for (const node of mutation.addedNodes) {
                if (node.nodeName !== "IFRAME") continue;
                const iframe = node as HTMLIFrameElement;
                const isZuora3DS2 =
                  iframe.id?.startsWith("zuora-3ds2-integration-") ||
                  (!iframe.id && iframe.style?.position === "fixed" && iframe.style?.zIndex === "9999");
                if (!isZuora3DS2) continue;
                closedForChallenge = true;
                if (!cancelled) {
                  setIsOpen(false);
                }
                bodyObserver?.disconnect();
                return;
              }
            }
          });
          bodyObserver.observe(document.body, { childList: true });
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const catchError = err instanceof Error ? err : new Error("Zuora createPaymentForm failed");
          rollbar.error(catchError, { action: "initializeZuoraPaymentForm" });
          const message = err instanceof Error
            ? err.message
            : typeof err === "string" ? err : JSON.stringify(err);
          setError(message || "Failed to load payment form");
          setIsLoading(false);
        }
      }
    };

    initializePaymentForm();

    return () => {
      cancelled = true;
      bodyObserver?.disconnect();
      const container = document.getElementById(ZUORA_CONTAINER_ID);
      if (container) {
        container.innerHTML = "";
      }
    };
  }, [isOpen]);

  const handleAddCardModalOpen = () => {
    trackEvent(
      ANALYTICS_EVENTS.FEATURE_ENGAGED,
      "Add Card button clicked",
      ANALYTICS_LOCATIONS.PAYMENT_METHODS
    );
    setIsOpen(true);
  };
  const handleAddCardModalClose = () => setIsOpen(false);

  const modal = (
    <AddCardModal
      isOpen={isOpen}
      onClose={handleAddCardModalClose}
      isLoading={isLoading}
      error={error}
      zuoraContainerId={ZUORA_CONTAINER_ID}
    />
  );

  return { handleAddCardModalOpen, modal };
};

export default useHandleAddCard;
